import { fg } from '@atlassian/jira-feature-gating';
import type { TimeTrackingConfig } from '@atlassian/jira-issue-shared-types';
import {
	secondsToTimeString,
	timeStringToSeconds,
	isTimeStringValid,
	isNumeric,
} from '@atlassian/jira-issue-view-common-utils';
import { getDefaultTimeTrackingUnit } from './common/utils/default-time-tracking-unit';

export const formatTimeString =
	(config: TimeTrackingConfig) =>
	(timeValue: number | null): string =>
		secondsToTimeString(config.daysPerWeek, config.hoursPerDay)(timeValue || 0);

export const appendTimeUnitIfMissing = (config: TimeTrackingConfig) => (value: string) => {
	let trimmedValue = value.trim();

	// If user doesn't type the valid format (m, h, d, or w), use default unit format set in the configuration.
	const isValidNumber = fg('support_comma_decimal_separator_in_time_fields')
		? isNumeric(trimmedValue.replaceAll(',', '.'))
		: isNumeric(trimmedValue);
	if (isValidNumber) {
		const { defaultUnit } = config;
		trimmedValue = `${trimmedValue}${getDefaultTimeTrackingUnit(defaultUnit)}`;
	}

	return trimmedValue;
};

export const isValidTimeString = (config: TimeTrackingConfig) => (value: string) => {
	const timeString = appendTimeUnitIfMissing(config)(value);
	return timeString === '' || isTimeStringValid(timeString);
};

export const parseTimeString =
	(config: TimeTrackingConfig) =>
	(value: string): number | null => {
		const timeString = appendTimeUnitIfMissing(config)(value);

		if (!isValidTimeString(config)(timeString)) {
			return 0;
		}

		return timeStringToSeconds(config.daysPerWeek, config.hoursPerDay)(timeString);
	};

// eslint-disable-next-line jira/export/no-external-exports
export { secondsToTimeString, timeStringToSeconds, getDefaultTimeTrackingUnit };
export { secondsToWholeMinuteTimeString } from './common/utils';
// eslint-disable-next-line jira/export/no-external-exports
export type { TimeTrackingConfig };
